import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { Star, Loader2, User, ThumbsUp, MessageSquare } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Star rating component
function StarRating({ rating, setRating, readonly = false, size = 'md' }) {
  const sizeClasses = {
    sm: 'w-5 h-5',
    md: 'w-8 h-8',
    lg: 'w-10 h-10'
  };

  return (
    <div className="flex gap-1" data-testid="star-rating">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => !readonly && setRating(star)}
          disabled={readonly}
          className={`${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'} transition-transform`}
          data-testid={`star-${star}`}
        >
          <Star
            className={`${sizeClasses[size]} ${
              star <= rating
                ? 'text-yellow-400 fill-yellow-400'
                : 'text-gray-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
}

// Previous reviews display
function PreviousReviews({ reviews, title }) {
  if (!reviews || reviews.length === 0) {
    return (
      <div className="text-center py-4 text-gray-500 text-sm">
        No previous reviews
      </div>
    );
  }

  return (
    <div className="space-y-3 max-h-48 overflow-y-auto">
      <h4 className="text-sm font-medium text-gray-700">{title}</h4>
      {reviews.map((review, idx) => (
        <div key={idx} className="p-3 bg-gray-50 rounded-lg border">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
              {review.reviewer_picture ? (
                <img src={review.reviewer_picture} alt="" className="w-full h-full object-cover" />
              ) : (
                <User className="w-4 h-4 text-gray-400" />
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">{review.reviewer_name}</p>
              <StarRating rating={review.rating} readonly size="sm" />
            </div>
            <span className="text-xs text-gray-400">
              {new Date(review.created_at).toLocaleDateString()}
            </span>
          </div>
          {review.review_text && (
            <p className="text-sm text-gray-600 pl-10">"{review.review_text}"</p>
          )}
        </div>
      ))}
    </div>
  );
}

export default function ReviewModal({ 
  isOpen, 
  onClose, 
  requestId, 
  serviceName,
  otherPartyName,
  otherPartyPicture,
  otherPartyId,
  reviewerType, // 'customer' or 'provider'
  onSubmitted
}) {
  const { getAuthHeaders } = useAuth();
  const [rating, setRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [previousReviews, setPreviousReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(true);

  useEffect(() => {
    if (isOpen && otherPartyId) {
      fetchPreviousReviews();
    }
  }, [isOpen, otherPartyId]);

  const fetchPreviousReviews = async () => {
    setLoadingReviews(true);
    try {
      const response = await axios.get(`${API}/reviews/user/${otherPartyId}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setPreviousReviews(response.data.reviews || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoadingReviews(false);
    }
  };

  const handleSubmit = async () => {
    if (rating < 1) {
      toast.error('Please select a rating');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/reviews/submit`,
        {
          request_id: requestId,
          rating,
          review_text: reviewText
        },
        { headers: getAuthHeaders(), withCredentials: true }
      );

      toast.success('Review submitted! Thank you for your feedback.');
      if (onSubmitted) onSubmitted();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const quickReviews = [
    { text: 'Great service!', rating: 5 },
    { text: 'Very professional', rating: 5 },
    { text: 'Fast and efficient', rating: 5 },
    { text: 'Good experience', rating: 4 },
    { text: 'Could be better', rating: 3 }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="review-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-500" />
            Rate Your Experience
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Service Info */}
          <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-lg">
            <div className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
              {otherPartyPicture ? (
                <img src={otherPartyPicture} alt={otherPartyName} className="w-full h-full object-cover" />
              ) : (
                <User className="w-7 h-7 text-gray-400" />
              )}
            </div>
            <div>
              <p className="font-semibold text-gray-900">{otherPartyName}</p>
              <p className="text-sm text-gray-500 capitalize">{serviceName?.replace('_', ' ')}</p>
            </div>
          </div>

          {/* Star Rating */}
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-3">
              How was your experience with {otherPartyName}?
            </p>
            <div className="flex justify-center">
              <StarRating rating={rating} setRating={setRating} size="lg" />
            </div>
            <p className="mt-2 text-lg font-medium text-gray-900">
              {rating === 5 ? 'Excellent!' : rating === 4 ? 'Great!' : rating === 3 ? 'Good' : rating === 2 ? 'Fair' : 'Poor'}
            </p>
          </div>

          {/* Quick Review Options */}
          <div className="flex flex-wrap gap-2 justify-center">
            {quickReviews.map((qr, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setReviewText(qr.text);
                  setRating(qr.rating);
                }}
                className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                  reviewText === qr.text
                    ? 'bg-blue-100 border-blue-300 text-blue-700'
                    : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                }`}
              >
                {qr.text}
              </button>
            ))}
          </div>

          {/* Review Text */}
          <div>
            <Textarea
              placeholder="Share more details about your experience (optional)"
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              rows={3}
              className="resize-none"
              data-testid="review-text"
            />
          </div>

          {/* Previous Reviews */}
          {!loadingReviews && previousReviews.length > 0 && (
            <div className="border-t pt-4">
              <PreviousReviews 
                reviews={previousReviews.slice(0, 3)} 
                title={`Previous reviews for ${otherPartyName}`}
              />
            </div>
          )}

          {/* Submit Button */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={onClose}
              disabled={submitting}
            >
              Skip for Now
            </Button>
            <Button
              className="flex-1 bg-blue-600 hover:bg-blue-700"
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="submit-review-btn"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <ThumbsUp className="w-4 h-4 mr-2" />
              )}
              Submit Review
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Hook to check for pending reviews
export function usePendingReviews() {
  const { getAuthHeaders, isAuthenticated } = useAuth();
  const [pendingReviews, setPendingReviews] = useState([]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchPendingReviews();
    }
  }, [isAuthenticated]);

  const fetchPendingReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/pending`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setPendingReviews(response.data.pending_reviews || []);
    } catch (error) {
      console.error('Error fetching pending reviews:', error);
    }
  };

  return { pendingReviews, refreshPendingReviews: fetchPendingReviews };
}

export { StarRating, PreviousReviews };
